import { useEffect, useMemo, useState } from "react";
import styles from "../Notifications.module.scss";
import searchIcon from "assets/images/admin/search-icon.svg";
import filterIcon from "assets/images/admin/filter_icon.svg";
import filterCloseXIcon from "assets/images/admin/btn_filter_closeX.svg";
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
  IconButton,
  TextField,
  Typography,
} from "@mui/material";
import {
  DatePicker,
  LocalizationProvider,
  PickersDay,
  PickersDayProps,
} from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { CalendarIcon, CloseIcon } from "assets/icons";
import { TrashIcon } from 'assets/icons_new/icons/grey/index';
import {
  MaterialReactTable,
  MRT_RowSelectionState,
  useMaterialReactTable,
} from "material-react-table";
import { useAppSelector, useDebounce } from "shared/lib/hooks/hooks";
import {
  useDeleteNotificationMutation,
} from "shared/queries/Audit/queries";
import dayjs, { Dayjs } from "dayjs";
import { toast } from "react-toastify";
import { ModalTitle } from "entities/admin/lib/MUIStyles/MUIStyles";
import ButtonWithProgress from "shared/ui/ButtonWIthProgress/ButtonWithProgress";
import { useNavigate } from "react-router-dom";
import { ADMIN_PANEL_PATH } from "pages/paths";
import { useTranslation } from "react-i18next";
import _ from "lodash";
import clsx from "clsx";
import { AuditTab } from "api/slice/auditDocumentsSlice/types";
import { getAuditDocument } from "api/thunk/auditDocuments/getActions/getAuditDocument";
import { useAppDispatch } from "shared/config/configureStore";
import { setAuditDocumentPage, setAuditDocumentPageSize, setAuditDocumentParams } from "api/slice/auditDocumentsSlice/auditDocumentsSlice";
import { getDocumentTypeLabel, getOperationTypeBadge } from "../auditDocuments/utils";
import DocumentTypeSelect from "../components/DocumentTypeSelect/DocumentTypeSelect";
import OperationTypeSelect from "../components/OperationTypeSelect";
import { DOCUMENT_OPTIONS, OPERATION_OPTIONS } from "../auditDocuments/constants";
import { CustomPagination } from "components/CustomPagination/CustomPagination";

const NotificationArchive = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const activeTab: AuditTab = 'archived';
  const { data, loading } = useAppSelector((state) => state.auditDocuments.auditDocument.archived.response);
  const { page, pageSize, searchTerm, operationType, documentType, dateFrom, dateTo, orderBy,
    direction, } = useAppSelector((state) => state.auditDocuments.auditDocument.archived.params);
  const debouncedSearch = useDebounce(searchTerm, 300);

  useEffect(() => {
    dispatch(
      getAuditDocument({
        tab: activeTab,
        params: {
          page: page,
          pageSize: pageSize,
          operationType: operationType && Number(operationType),
          documentType: documentType && Number(documentType),
          dateFrom: dateFrom && dateFrom,
          dateTo: dateTo && dateTo,
          orderBy,
          direction,
          searchTerm: debouncedSearch,
          isArchived: true,
        },
      })
    );
  }, [
    dispatch,
    activeTab,
    page,
    pageSize,
    debouncedSearch,
    dateFrom,
    dateTo,
    operationType,
    documentType,
    orderBy,
    direction,
  ]);

  const handlePageChange = (
    value: number
  ) => {
    dispatch(setAuditDocumentPage({ tab: activeTab, page: value }))
  }

  const handlePageSizeChange = (newSize: number) => {
    dispatch(setAuditDocumentPageSize({ tab: activeTab, pageSize: newSize }));
    dispatch(setAuditDocumentPage({ tab: activeTab, page: 1 }));
    dispatch(
      getAuditDocument({
        tab: activeTab,
        params: {
          page: 1,
          pageSize: newSize,
          isArchived: true,
          orderBy: null,
          searchTerm: null,
        },
      })
    );
  };

  const [selectedRows, setSelectedRows] = useState<MRT_RowSelectionState>({});
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [openFilter, setOpenFilter] = useState(false);

  const hasSelected = Object.keys(selectedRows).length > 0;

  const [deleteNotification, { isLoading: isDeleteLoading }] =
    useDeleteNotificationMutation();

  const deleteNotificationsHandler = async () => {
    try {
      await deleteNotification(selectedRows).unwrap();
      toast.success("Уведомления успешно удалены", {
        position: "top-right",
        autoClose: 5000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
        theme: "colored",
      });
      setSelectedRows({});
    } catch (error: any) {
      toast.error(error?.data?.detail, {
        position: "top-right",
        autoClose: 5000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
        theme: "colored",
      });
    } finally {
      setDeleteModalOpen(false);
    }
  };

  const columns = useMemo(
    () => [
      {
        accessorKey: "fullName",
        header: `${t("notificationsPage.fullName")}`,
        enableSorting: false,
        size: 310,
        Cell: (value: any) => (
          <>
            <Typography fontSize={"14px"} fontWeight={600} color="#3E444A">
              {value.renderedCellValue}
            </Typography>
            <Typography fontSize={"12px"} fontWeight={500} color="#3E444A">
              {value.row.original.email}
            </Typography>
          </>
        ),
      },
      {
        accessorKey: "operationType",
        header: `${t("notificationsPage.actionType")}`,
        enableSorting: false,
        size: 155,
        Cell: (value: any) => getOperationTypeBadge(value.renderedCellValue, t),
      },
      {
        accessorKey: "timestamp",
        header: `${t("notificationsPage.dateTime")}`,
        enableSorting: true,
        size: 130,
        Cell: (value: any) =>
          dayjs(value.renderedCellValue.slice(0, 19) + "Z").format(
            "DD/MM/YYYY - HH:mm"
          ),
      },

      {
        accessorKey: "documentType",
        header: `${t("notificationsPage.documentType")}`,
        enableSorting: false,
        size: 265,
        Cell: (value: any) => getDocumentTypeLabel(value.renderedCellValue, t),
      },
    ],
    []
  );

  const sorting = useMemo(() => {
    if (orderBy == null || direction == null) return [];
    return [{ id: orderBy, desc: direction === 0 }];
  }, [orderBy, direction]);

  const table = useMaterialReactTable({
    //@ts-ignore
    columns,
    data: data?.items || [],
    enableTopToolbar: false,
    enableColumnActions: false,
    manualPagination: true,
    initialState: { density: "comfortable" },
    enablePagination: false,
    manualSorting: true,
    state: {
      isLoading: loading,
      showLoadingOverlay: isDeleteLoading,
      sorting,
      rowSelection: selectedRows,
    },
    onSortingChange: (updater) => {
      const newSorting = typeof updater === 'function' ? updater([]) : updater;

      if (newSorting.length > 0) {
        dispatch(setAuditDocumentParams({
          tab: activeTab,
          params: {
            page: 1,
            orderBy: newSorting[0].id,
            direction: newSorting[0].desc ? 0 : 1,
          },
        }));
      } else {
        dispatch(setAuditDocumentParams({
          tab: activeTab,
          params: {
            page: 1,
            orderBy: null,
            direction: null,
          },
        }));
      }
    },
    getRowId: (row) => {
      return row.id?.toString();
    },
    enableRowSelection: true,
    onRowSelectionChange: setSelectedRows,
    rowCount: data?.totalCount,
    muiTableHeadRowProps: () => ({
      style: {
        background: "#ECF2F7",
        borderRadius: "20px",
        border: "none",
        boxShadow: "none",
      },
    }),
    muiTablePaperProps: {
      style: {
        borderTopLeftRadius: "15px",
        borderTopRightRadius: "15px",
        boxShadow: "none ",
      },
    },
    muiTableHeadCellProps: {
      sx: {
        height: "10px",
        fontSize: "12px",
        lineHeight: "20px",
        fontWeight: 600,
        fontFamily: "Raleway",
        border: "none",
        "& .MuiTableSortLabel-icon": {
          color: "black !important",
          opacity: 1,
        },
      },
    },
    muiBottomToolbarProps: {
      style: {
        border: "none",
        boxShadow: "none ",
      },
    },
    muiTableBodyProps: {
      sx: {
        //stripe the rows, make odd rows a darker color
        "& tr:nth-of-type(even):not(.Mui-selected) > td": {
          backgroundColor: "#F5FAFF",
        },
      },
    },
    muiTableBodyCellProps: {
      style: {
        fontSize: "12px",
        fontVariantNumeric: "lining-nums proportional-nums",
        textOverflow: "ellipsis",
        fontFeatureSettings: '"liga" off',
        lineHeight: "20px",
        fontWeight: 500,
        fontFamily: "Raleway",
        color: "#3E444A",
        border: "none",
      },
    },
    muiTableBodyRowProps: ({ row }) => ({
      onClick: (e: any) => {
        e.stopPropagation();
        navigate(
          `/${ADMIN_PANEL_PATH}/users/disabled_people/${row.original.userId}`
        );
        row.getToggleSelectedHandler()(row.original.id);
      },
      sx: {
        cursor: "pointer",
      },
    }),
    muiPaginationProps: {},
    localization: {
      rowsPerPage: "Строк в таблице",
    },
  });

  const handleStartDateChange = (newValue: Dayjs | null) => {
    dispatch(
      setAuditDocumentParams({
        tab: activeTab,
        params: {
          page: 1,
          ...(newValue && {
            dateFrom: newValue.format("YYYY-MM-DD")
          }),
        },
      })
    );
  };

  const handleEndDateChange = (newValue: Dayjs | null) => {
    dispatch(
      setAuditDocumentParams({
        tab: activeTab,
        params: {
          page: 1,
          ...(newValue && {
            dateTo: newValue.format("YYYY-MM-DD")
          }),
        },
      })
    );
  };

  const isDateInRange = (date: Dayjs | null) => {
    if (!date || !dateFrom || !dateTo) return false;
    return date.isSame(dateFrom, 'day') || date.isSame(dateTo, 'day') || date.isAfter(dateFrom) && date.isBefore(dateTo);
  };

  const documentOptions = DOCUMENT_OPTIONS.map((item) => ({
    id: item.id,
    name: t(item.nameKey),
  }));

  const actionOptions = OPERATION_OPTIONS.map((item) => ({
    id: item.id,
    name: t(item.nameKey),
  }));

  return (
    <section>
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: "24px",
          justifyContent: "space-between",
          mb: "24px",
        }}
      >
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: { xs: "flex-start", sm: "center" },
            flexDirection: { xs: "column", sm: "row" },
            gap: "16px",
          }}
        >
          <div className={styles.box_inputSearch} style={{ width: "100%" }}>
            <input
              type="text"
              placeholder="Введите ФИО или почту"
              value={searchTerm ?? ""}
              onChange={(e) => {
                const value = e.target.value;
                dispatch(setAuditDocumentParams({
                  tab: activeTab,
                  params: { searchTerm: value, page: 1 }
                }));
              }}
              className="input-adminPanel"
            />
            <img src={searchIcon} alt="Поиск" />
          </div>
          <Button
            onClick={() => {
              setDeleteModalOpen(true);
            }}
            color="error"
            disabled={isDeleteLoading || Object.keys(selectedRows).length <= 0}
            startIcon={<TrashIcon className={clsx(styles.icon, {
              [styles.active]: hasSelected
            })} />}
            sx={{
              textTransform: "none",
              padding: "6px 24px",
              fontSize: "14px",
              width: { xs: "100%", sm: "auto" },
              fontWeight: 500,
              borderRadius: "12px",
              border: `2px solid ${hasSelected ? "#e53935" : "#C0D4E3"}`,
              color: hasSelected ? "#e53935" : "#C0D4E3",
              cursor: hasSelected ? "pointer" : "not-allowed",
              "&:hover": {
                backgroundColor: hasSelected ? "rgba(229, 57, 53, 0.08)" : "transparent",
                borderColor: hasSelected ? "#D32F2F" : "#C0D4E3",
              },
            }}
          >
            {isDeleteLoading && (
              <CircularProgress size={24} className={styles.buttonProgress} />)}
            {t("buttons.delete")}
          </Button>

          <Box sx={{ width: { xs: "100%", sm: "auto" } }}>
            <button
              className={styles.btn_filter}
              onClick={() => setOpenFilter(!openFilter)}
              style={{ whiteSpace: "nowrap", width: "100%" }}
            >
              <img
                src={openFilter ? filterCloseXIcon : filterIcon}
                alt={openFilter ? `${t("filters.hideFilters")}` : `${t("filters.showFilters")}`}
              />
              <span>
                {openFilter ? `${t("filters.hideFilters")}` : `${t("filters.showFilters")}`}
              </span>
            </button>
          </Box>
        </Box>
        {(openFilter || operationType || documentType || dateFrom || dateTo) && (
          <Box
            sx={{
              display: "flex",
              flexDirection: { xs: "column", md: "row" },
              flexWrap: "wrap",
              width: "100%",
              gap: "16px",
              label: { flex: "auto" },
            }}
          >
            <Box
              display="flex"
              flexDirection="column"
              width={{ xs: "100%", md: "33%" }}
            >
              <Box display="flex" gap="16px" marginBottom="4px">
                <Box flex={1} display="flex" justifyContent="space-between" alignItems="center">
                  <Typography
                    component="label"
                    htmlFor={"dateFilter"}
                    fontSize="12px"
                    fontWeight={500}
                    color="#68717B"
                  >
                    {t("notificationsPage.date")}
                  </Typography>
                  {dateFrom && (
                    <button
                      className={styles.resetSelectedItem}
                      type="button"
                      onClick={() => {
                        dispatch(
                          setAuditDocumentParams({
                            tab: activeTab,
                            params: {
                              dateFrom: null,
                              page: 1,
                            },
                          })
                        );
                      }}
                    >
                      {t("marathonResults.resetFilter")}
                    </button>
                  )}
                </Box>
                <Box flex={1} display="flex" justifyContent="flex-end" alignItems="center">
                  {dateTo && (
                    <button
                      className={styles.resetSelectedItem}
                      type="button"
                      onClick={() => {
                        dispatch(
                          setAuditDocumentParams({
                            tab: activeTab,
                            params: {
                              dateTo: null,
                              page: 1,
                            },
                          })
                        );
                      }}
                    >
                      {t("marathonResults.resetFilter")}
                    </button>
                  )}
                </Box>
              </Box>
              <Box display="flex" gap="16px">
                <Box flex={1}>
                  <LocalizationProvider
                    dateAdapter={AdapterDayjs}
                    className={styles.datePicker}
                  >
                    <DatePicker
                      components={{
                        OpenPickerIcon: CalendarIcon,
                      }}
                      inputFormat="DD.MM.YYYY"
                      value={dateFrom}
                      minDate={undefined}
                      maxDate={dateTo ? dayjs(dateTo) : undefined}
                      onChange={handleStartDateChange}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          placeholder="Начало"
                          autoComplete="off"
                          size="small"
                          sx={{
                            width: "100%",
                            "& .MuiOutlinedInput-notchedOutline": {
                              borderRadius: "10px",
                              border: "1px solid #C0D4E3 !important",
                            },
                            "& .MuiInputBase-input": {
                              padding: "8px 0 8px 10px",
                            },
                            "&::placeholder": {
                              fontStyle: "italic",
                            },
                          }}
                        />
                      )}
                      renderDay={(
                        day: Dayjs,
                        selectedDays: Dayjs[],
                        pickersDayProps: PickersDayProps<Dayjs>
                      ) => {
                        const isSelected = isDateInRange(day);
                        const isToday = day.isSame(dayjs(), "day");

                        return (
                          <PickersDay
                            {...pickersDayProps}
                            day={day}
                            className={
                              isSelected
                                ? "custom-day"
                                : pickersDayProps.className
                            }
                            selected={isSelected}
                            style={{
                              ...(pickersDayProps?.style || {}),
                              border: isToday
                                ? "none"
                                : pickersDayProps?.style?.border,
                              fontWeight: isToday
                                ? "normal"
                                : pickersDayProps?.style?.fontWeight,
                            }}
                          />
                        );
                      }}
                    />
                  </LocalizationProvider>
                </Box>
                <Box flex={1}>
                  <LocalizationProvider dateAdapter={AdapterDayjs} className={styles.datePicker}>
                    <DatePicker
                      components={{
                        OpenPickerIcon: CalendarIcon,
                      }}
                      inputFormat="DD.MM.YYYY"
                      value={dateTo}
                      minDate={dateFrom ? dayjs(dateFrom) : undefined}
                      maxDate={undefined}
                      onChange={handleEndDateChange}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          placeholder="Конец"
                          autoComplete="off"
                          size="small"
                          sx={{
                            width: "100%",
                            "& .MuiOutlinedInput-notchedOutline": {
                              borderRadius: "10px",
                              border: "1px solid #C0D4E3 !important",
                            },
                            "& .MuiInputBase-input": {
                              padding: "8px 0 8px 10px",
                            },
                            "&::placeholder": {
                              fontStyle: "italic",
                            },
                          }}
                        />
                      )}
                      renderDay={(
                        day: Dayjs,
                        selectedDays: Dayjs[],
                        pickersDayProps: PickersDayProps<Dayjs>
                      ) => {
                        const isSelected = isDateInRange(day);
                        const isToday = day.isSame(dayjs(), "day");

                        return (
                          <PickersDay
                            {...pickersDayProps}
                            day={day}
                            className={
                              isSelected
                                ? "custom-day"
                                : pickersDayProps.className
                            }
                            selected={isSelected}
                            style={{
                              ...(pickersDayProps?.style || {}),
                              border: isToday
                                ? "none"
                                : pickersDayProps?.style?.border,
                              fontWeight: isToday
                                ? "normal"
                                : pickersDayProps?.style?.fontWeight,
                            }}
                          />
                        );
                      }}
                    />
                  </LocalizationProvider>
                </Box>
              </Box>
            </Box>
            <Box sx={{ width: { xs: "100%", md: "auto" }, flex: { md: 1 } }}>
              <DocumentTypeSelect
                placeholder="Выберите документы"
                nameLabel="Тип документа"
                arr={documentOptions}
                selected={documentType ?? ""}
                setSelected={(value) => {
                  dispatch(setAuditDocumentParams({ tab: activeTab, params: { documentType: value, page: 1 } }));
                }}
              />
            </Box>

            <Box sx={{ width: { xs: "100%", md: "auto" }, flex: { md: 1 } }}>
              <OperationTypeSelect
                placeholder="Выберите действие"
                nameLabel="Тип действия"
                arr={actionOptions}
                selected={operationType ?? ""}
                setSelected={(value) => {
                  dispatch(setAuditDocumentParams({ tab: activeTab, params: { operationType: value, page: 1 } }));
                }}
              />
            </Box>
          </Box>
        )}
      </Box>

      {!_.isEmpty(selectedRows) && (
        <div>
          <p>
            {t("notificationsPage.selected")} {Object.keys(selectedRows).length}
          </p>
        </div>
      )}

      <MaterialReactTable table={table || []} />
      <CustomPagination
        totalCount={data?.totalCount ?? 0}
        page={page ?? 1}
        pageSize={pageSize ?? 10}
        onPageChange={handlePageChange}
        onPageSizeChange={handlePageSizeChange}
      />
      <Dialog
        open={deleteModalOpen}
        onClose={() => setDeleteModalOpen(false)}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
        sx={{
          fontFamily: "Raleway",
          ".MuiDialog-paper": {
            width: "426px",
            borderRadius: "16px",
            padding: "24px",
          },
        }}
      >
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <DialogTitle id="modal-modal-title" sx={ModalTitle}>
            Удалить уведомление?
          </DialogTitle>
          <IconButton onClick={() => setDeleteModalOpen(false)}>
            <CloseIcon />
          </IconButton>
        </Box>

        <DialogContent sx={{ paddingInline: 0 }}>
          <DialogContentText id="alert-dialog-description">
            Вы уверены что хотите удалить уведомления? При удалении невозможно
            восстановить данные
          </DialogContentText>
        </DialogContent>
        <Divider />
        <DialogActions
          sx={{
            justifyContent: "flex-start",
            paddingInline: 0,
            marginTop: "16px",
          }}
        >
          <ButtonWithProgress
            loading={isDeleteLoading}
            disabled={isDeleteLoading}
            variant="contained"
            onClick={deleteNotificationsHandler}
            autoFocus
            sx={{ borderRadius: "16px" }}
          >
            Удалить
          </ButtonWithProgress>
          <Button onClick={() => setDeleteModalOpen(false)}>Отмена</Button>
        </DialogActions>
      </Dialog>
    </section>
  );
};

export default NotificationArchive;
